import { ButtonBase, styled } from '@mui/material';
import { useAuth } from 'contexts/auth';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Drawer, Icon, IconButton, Logo, Stack, Text } from 'shell/components';

const TimelyGraderLogo = () => {
    const history = useHistory();
    const { guard } = useNavigationGuard();

    return (
        <CustomButtonBase aria-label={'TimelyGrader'} onClick={() => guard(() => history.push('/'))}>
            <Logo />
        </CustomButtonBase>
    );
};

const Mobile = ({ navOptions, dropdownOptions }) => {
    const { changeAccount } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const { guard } = useNavigationGuard();

    const [open, setOpen] = useState(false);

    navOptions = [
        ...(navOptions || []).filter((navOption) => navOption.key !== 'more'),
        ...((navOptions || []).find((navOption) => navOption.key === 'more')?.options || []).map(({ key, value }) => {
            return { key, text: value, link: `/${key}` };
        }),
    ];

    const handleClick = (to) => {
        guard(() => {
            history.push(to);
            setOpen(false);
        });
    };

    const handleClickOption = ({ key }) => {
        if (key === 'profile') {
            guard(() => {
                history.push('/profile');
                setOpen(false);
            });
        }
        if (key === 'course-settings') {
            guard(() => {
                history.push('/course-settings');
                setOpen(false);
            });
        }
        if (key === 'switch-course') {
            guard(() => {
                changeAccount();
                setOpen(false);
            });
        }
        if (key === 'course-dashboard') {
            guard(() => {
                history.push('/');
                setOpen(false);
            });
        }
    };

    const renderNavOptions = () => {
        return navOptions.map((navOption) => {
            return (
                <Box key={navOption.key} sx={{ position: 'relative' }}>
                    <Button
                        sx={{ minWidth: 'unset' }}
                        color={location.pathname === navOption.link ? 'primary' : 'neutral'}
                        size={'large'}
                        startIcon={navOption.icon ? <Icon kind={navOption.icon} /> : null}
                        variant={'tertiary'}
                        onClick={() => handleClick(navOption.link)}
                    >
                        {navOption.text}
                    </Button>
                </Box>
            );
        });
    };

    const activeOption = navOptions.find(({ link }) => location.pathname.startsWith(link));

    const getPageHeader = () => {
        if (location.pathname.includes('/course-settings')) return 'Course Settings';
        return activeOption?.text;
    };

    return (
        <CustomStack alignItems={'center'} direction={'row'}>
            <TimelyGraderLogo />
            <Text variant={'p1'} weight={'semibold'}>
                {getPageHeader()}
            </Text>
            <IconButton
                aria-label={'navigation menu'}
                data-cy={'nav-hamburger-menu-button'}
                sx={{ mr: -2 }}
                kind={open ? 'exit' : 'hamburger'}
                size={'medium'}
                variant={'square'}
                onClick={() => setOpen((current) => !current)}
            />
            <Drawer device={'mobile'} open={open} onClose={() => setOpen(false)}>
                <Stack>
                    <IconButton
                        sx={{ alignSelf: 'flex-end' }}
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setOpen(false)}
                    />
                    <Stack alignItems={'flex-start'} spacing={2}>
                        {renderNavOptions()}
                    </Stack>
                    <Stack
                        sx={{ position: 'absolute', bottom: (theme) => theme.spacing(5) }}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        {dropdownOptions
                            .filter((option) => !['user', 'divider'].includes(option.key))
                            .map((option) => (
                                <Button
                                    key={option.key}
                                    color={'neutral'}
                                    size={'small'}
                                    variant={'tertiary'}
                                    onClick={() => handleClickOption(option)}
                                >
                                    {option.value}
                                </Button>
                            ))}
                    </Stack>
                </Stack>
            </Drawer>
        </CustomStack>
    );
};

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        '&:focus-visible': {
            borderRadius: '6px',
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        justifyContent: 'space-between',
        width: '100%',
        height: '64px',
        padding: theme.spacing(0, 5),
    };
});

export default Mobile;
